import React, { useState, useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import UploadDealerPricing from "./UploadDealerPricing";
import UploadDealerPriceClient from "../../../../shared/clients/UploadDealerPriceClient";
import ConfirmDialog from "../../../../shared/confirmDialog/ConfirmDialog";

const DealerPricing = (props) => {
  const [dealerInventoryPrices, setDealerInventoryPrices] = useState([]);
  const [allDealerInventoryPrices, setAllDealerInventoryPrices] = useState([]);
  const [hideUsdPrice, setHideUsdPrice] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteVin, setDeleteVin] = useState("");
  const [sortExpression, setSortExpression] = useState(undefined);
  const [filters, setFilters] = useState([]);
  const [waitingForDealerData, setWaitingForDealerData] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const dialogProps = useMemo(
    () => ({
      confirm: props.t("UploadDealerPrice.confirm"),
      cancel: props.t("UploadDealerPrice.cancel"),
      bodyText: props.t("UploadDealerPrice.bodyTextConfirmDelete"),
    }),
    [props]
  );

  useEffect(() => {
    if (props.user && props.user.market !== "ARG") {
      setHideUsdPrice(true);
    }

    if (
      props.user &&
      props.user.dealerGroup &&
      props.user.dealerGroup.length > 0
    ) {
      loadDealerInventoryPricingHistory();
    } else {
      setWaitingForDealerData(true);
    }
  }, []);

  useEffect(() => {
    if (waitingForDealerData && props.user?.dealerGroup?.length > 0) {
      setWaitingForDealerData(false);
      loadDealerInventoryPricingHistory();
    }
  }, [props.user?.dealerGroup?.length, waitingForDealerData]);

  const handleDeleteConfirmation = () => {
    const vin = deleteVin;
    props.hideOrShow(true);
    UploadDealerPriceClient.deleteInventoryPrices(vin, props.user)
      .then((data) => {
        props.hideOrShow(false);
        if (data.message.code === "200") {
          setOpenDeleteDialog(false);
          loadDealerInventoryPricingHistory();
        }
      })
      .catch(() => {
        props.hideOrShow(false);
      });
  };

  const handleClose = () => {
    setOpenDeleteDialog(false);
    setDeleteVin("");
  };

  const uncheckEverything = () => {
    const prices = dealerInventoryPrices;
    prices.forEach((price) => {
      price.checked = false;
    });
    setDealerInventoryPrices(prices);
  };

  const reloadWithFilteredData = (expression) => {
    const records = [...allDealerInventoryPrices];
    if (expression.property === "all") {
      setDealerInventoryPrices(records);
    } else {
      const filteredData = records.filter(
        (currentRecord) =>
          currentRecord[expression.property] === expression.value
      );
      setDealerInventoryPrices(filteredData);
    }
  };

  const loadDealerInventoryPricingHistory = (expression) => {
    if (expression) {
      const records = dealerInventoryPrices;
      const sortedData = getSortedData(expression, records);
      setDealerInventoryPrices(sortedData);
      setSortExpression(expression);
      setIsDataLoaded(true);
      props.hideOrShow(false);
    } else {
      if (!props.user || !props.user.dealerGroup) {
        setDealerInventoryPrices([]);
        setAllDealerInventoryPrices([]);
        setIsDataLoaded(true);
        props.hideOrShow(false);
        return;
      }

      UploadDealerPriceClient.retrieveDealerInventoryPrices(props.user)
        .then((data) => {
          const inventoryData =
            data?.dealerPricesDataList?.[0]?.dealerInvPriceData || [];

          if (sortExpression) {
            const sortedData = getSortedData(sortExpression, inventoryData);
            setDealerInventoryPrices(sortedData);
            setAllDealerInventoryPrices(sortedData);
          } else {
            setDealerInventoryPrices(inventoryData);
            setAllDealerInventoryPrices(inventoryData);
          }
        })
        .catch((error) => {
          console.error("Failed to load dealer inventory pricing:", error);
          setDealerInventoryPrices([]);
          setAllDealerInventoryPrices([]);
        })
        .finally(() => {
          setIsDataLoaded(true);
          getFilterValues();
          props.hideOrShow(false);
        });
    }
  };

  const getFilterValues = () => {
    const records = allDealerInventoryPrices;

    if (!records || !Array.isArray(records)) {
      setFilters([
        {
          text: props.t("UploadDealerPrice.allVehicles"),
          value: "all.all",
        },
      ]);
      return;
    }

    const filtersList = [
      { text: props.t("UploadDealerPrice.allVehicles"), value: "all.all" },
    ];

    if (props.user && props.user.market !== "MEX") {
      let model = {
        text: props.t("UploadDealerPrice.zeroPrice"),
        value: "currencyAdjustedPrice.0",
      };
      filtersList.push(model);
      model = {
        text: props.t("UploadDealerPrice.unPublishedInventory"),
        value: "published.false",
      };
      filtersList.push(model);
    }

    const flags = [],
      recordCount = records.length;

    for (let i = 0; i < recordCount; i++) {
      if (flags[records[i].modelYear]) {
        continue;
      }
      flags[records[i].modelYear] = true;
      const model = {
        text:
          props.t("UploadDealerPrice.modelYear") + " " + records[i].modelYear,
        value: "modelYear." + records[i].modelYear,
      };
      filtersList.push(model);
    }

    setFilters(filtersList);
  };

  const getSortedData = (sortExpr, records) => {
    const sortProperty = sortExpr.split(".")[0];
    const sortDirection = sortExpr.split(".")[1];

    records.sort(dynamicSort(sortProperty, sortDirection));
    return records;
  };

  const dynamicSort = (property, dir) => {
    let sortOrder = 1;
    if (dir === "DSC") {
      sortOrder = -1;
    }
    return function (firstElement, secondElement) {
      const result =
        firstElement[property] < secondElement[property]
          ? -1
          : firstElement[property] > secondElement[property]
          ? 1
          : 0;
      return result * sortOrder;
    };
  };

  const showInventoryConfirmation = (v) => {
    setOpenDeleteDialog(true);
    setDeleteVin(v);
  };

  return (
    <div>
      {isDataLoaded && (
        <>
          <UploadDealerPricing
            {...props}
            enableUpload={true}
            loadDealerInventoryPricingHistory={(filterExpression) =>
              loadDealerInventoryPricingHistory(filterExpression)
            }
            dealerInventoryPrices={dealerInventoryPrices}
            hideUsdPrice={hideUsdPrice}
            uncheckEverything={uncheckEverything}
            showInventoryConfirmation={showInventoryConfirmation}
            filters={filters}
            reloadWithFilteredData={(filterExpression) =>
              reloadWithFilteredData(filterExpression)
            }
          />
          <ConfirmDialog
            open={openDeleteDialog}
            dialogProps={dialogProps}
            onConfirm={handleDeleteConfirmation}
            onCancel={handleClose}
            onClose={handleClose}
            boldTitle={false}
            {...props}
          />
        </>
      )}
    </div>
  );
};

export default withTranslation("emp")(DealerPricing);
